import { OPTISIGNS_LOGO } from '@/lib/brand-assets';
import { useRef } from 'react';
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { NavLink } from './NavLink';
import { GlobalSearchBar } from './GlobalSearchBar';
import { UserMenu } from './UserMenu';
import { NotificationDropdown } from './NotificationDropdown';
import { HelpDropdown } from './HelpDropdown';

import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useCanManageOptisound } from '@/hooks/useCanManageOptisound';
import { useFeatureHighlight } from '@/hooks/useFeatureHighlight';
import { useNavBreakpoints } from '@/hooks/useNavBreakpoints';
import { useMdmKillSwitch } from '@/hooks/useMdmKillSwitch';
import { useHideOptiSignsBranding } from '@/features/branding/useHideOptiSignsBranding';
import { passesDevicesTabTargeting } from '@/lib/mdm-access';
import { useAppVariant } from '@/lib/app-variant';
import { ROLE_TYPES } from '@scio3/shared-types';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@scio3/ui';

type TopNavigationProps = {
  /** Onboarding lock: render the FULL nav (logo, tabs, search, account) but
   *  dimmed and non-interactive, so users can't navigate away mid-survey.
   *  Also swaps the later tabs and the search/help/bell cluster for grey
   *  placeholders — a brand-new user is not shown Templates / Engage / Devices
   *  / Analytics on their first screen. */
  locked?: boolean;
  /** Non-interactive but fully rendered: every real tab, label and control stays
   *  exactly as it is, just unclickable. Used behind the lapsed-account paywall,
   *  where the whole point is that the user recognises their own portal — the
   *  `locked` placeholders would read as "features removed" at precisely the
   *  wrong moment. No dimming either, for the same reason. */
  inert?: boolean;
};

/** Placeholder bar widths shown in place of the nav tabs during onboarding. */
const MINIMAL_NAV_PLACEHOLDERS = ['w-16', 'w-20', 'w-14', 'w-16'];

export function TopNavigation({ locked = false, inert = false }: TopNavigationProps = {}) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);
  const collapseLevel = useNavBreakpoints(navRef);
  const { pathname } = useLocation();
  const company = useAuthStore((s) => s.company);
  // Devices-tab targeting reads the account OWNER's email (stable regardless of
  // which user logs in — sub-users may be username-only with no email), not the
  // logged-in user's. See `passesDevicesTabTargeting`.
  const ownerEmail = useAuthStore((s) => s.me?.ownerEmail);
  const permissionsLoaded = useAuthStore((s) => s.me != null);
  const { isRootAdmin, isRootSuperAdmin, teamRole } = usePermissions();
  const variant = useAppVariant();
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;
  const hideOptiSignsBranding = useHideOptiSignsBranding();
  // White-label sub-flags driving left-nav visibility (scio1 topNavigation.html parity).
  const removeOptiSignsBranding = !!company?.brandingData?.removeOptiSignsBranding;
  const hideProofofPlay = !!company?.brandingData?.hideProofofPlay;
  // Templates/Designer gate (scio1: shown only when customBrandCanvas). Default true (undefined → show).
  const customBrandCanvas = company?.brandingData?.customBrandCanvas !== false;
  // feature.mdm is a new OptiEdge-Phase-1 flag not yet in the CompanyFeatures
  // TypeScript type (packages/shared-types finalized in Phase 3). Narrow
  // structural read so we stay type-safe without editing the shared type.
  const hasMdmFeature = (company?.feature as Record<string, unknown> | undefined)?.mdm === true;
  // Global MDM kill-switch (appCfgs.mdmEnabled, appType: 'scio'). When true, Devices
  // tab is visible to everyone on the optisigns variant. When false/absent, only
  // accounts with company.feature.mdm=true see it (us + early adopters).
  const mdmKillSwitchOn = useMdmKillSwitch();

  // Wait for permissions to load before showing restricted nav items.
  // While me is loading, teamRole is undefined → hide restricted items to prevent
  // flash of forbidden links for read-only users (they appear then disappear).
  const isRestrictedRole =
    teamRole === ROLE_TYPES.READ_ONLY ||
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL ||
    teamRole === ROLE_TYPES.CONTENT_APPROVAL;

  const useNativeEngage = import.meta.env.VITE_USE_NATIVE_ENGAGE === 'true';
  const useNativeOptiDev = import.meta.env.VITE_USE_NATIVE_OPTIDEV === 'true';
  const useNativeOptisound = import.meta.env.VITE_USE_NATIVE_OPTISOUND === 'true';
  // Engage visible for all roles EXCEPT CONTENT_PROPOSAL & CONTENT_APPROVAL
  // (READ_ONLY users can browse but not edit)
  const isEngageRestrictedRole =
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL || teamRole === ROLE_TYPES.CONTENT_APPROVAL;
  const showEngage =
    permissionsLoaded &&
    !isEngageRestrictedRole &&
    variant === 'optisigns' &&
    (company?.feature?.engage || useNativeEngage) &&
    // scio1: hide Engage only when white-label AND OptiSigns branding is removed.
    (!isWhiteLabel || !removeOptiSignsBranding);
  const showTemplates =
    permissionsLoaded && !isRestrictedRole && variant === 'optisigns' && customBrandCanvas;
  // FDP gate for the OptiDev tab. `company.payment.firstDevicePaired` is set
  // once (to the device id) by the backend on first pair and is never reset —
  // so plain truthiness is the correct "has ever paired" gate (it never holds
  // the 'not_triggered' sentinel the other PLG flags use). Same field the
  // firstDevicePaired analytics check uses.
  const hasPairedDevice = !!company?.payment?.firstDevicePaired;
  // Active OptiSound (background music) add-on — its OWN trial or paid subscription.
  // An expired add-on reads as neither isActive nor isTrial, so it drops out here.
  const backgroundMusicAddOn = company?.payment?.subscriptionIdAddOns?.find(
    (a) => a.key === 'backgroundmusic',
  );
  const hasActiveBackgroundMusic =
    !!backgroundMusicAddOn && (!!backgroundMusicAddOn.isActive || !!backgroundMusicAddOn.isTrial);
  // OptiSound tab shows ONLY while the Background Music add-on is active (its
  // own trial or paid subscription) — accounts that never activated it, or whose
  // trial expired without subscribing, never see the tab (#1824). There is no
  // discovery path here; activation happens from the Edit Screen BM section.
  // White-label: OptiSound is OptiSigns-attributed, so it hides when the
  // customer checked "Remove OptiSigns Branding" — same gate as OptiDev/Engage.
  // Owner / Super Admin ONLY: OptiSound is company-wide (locations, zones and
  // content belong to the whole account, not a team), so it is limited to the
  // roles that already see the entire account. Enforced server-side too — the
  // OptiSound API rejects non-admin embed callers, so hiding the tab is the
  // affordance, not the security boundary.
  // Shared with the Background Music dialog, which points AT this tab — see
  // useCanManageOptisound.
  const showOptisound = useCanManageOptisound();
  // OptiDev is OptiSigns-attributed: on white-label/custom-domain portals it stays
  // available unless the customer checked "Remove OptiSigns Branding" — same
  // white-label gate as Engage above. Gated on FDP (has ever paired a device).
  const showOptiDev =
    permissionsLoaded &&
    !isRestrictedRole &&
    useNativeOptiDev &&
    hasPairedDevice &&
    (!isWhiteLabel || !removeOptiSignsBranding);
  // MDM is admin-only (spec 19 — Owner / Super Admin only in Phase A). The
  // route guard `MdmFeatureRoute` enforces this server-trip too; hiding the
  // top-nav link is the matching frontend half. Phase B will swap the gate
  // for a custom MDM Admin role check.
  //
  // Targeting gates the Devices *nav tab* for ALL optisigns accounts — the OWNER
  // must have a business email AND the account must have purchased 5 or more
  // screen licenses. This applies whether the account is entitled/whitelisted via
  // company.feature.mdm (ProPlus/Engage/Enterprise + Sales-onboarded) OR surfaced
  // through the appCfgs.mdmEnabled kill-switch broad rollout — an entitled account
  // that fails targeting (free-email owner or <5 licenses) no longer sees the tab.
  // This hides the nav ENTRY only: the route guard (MdmFeatureRoute) doesn't check
  // targeting, and the screen-page "..." → Device Management item is the
  // unrestricted fallback — both still reach /app/mdm by URL. Non-entitled targets
  // that match see the tab and hit the paywall (upsell). See
  // `passesDevicesTabTargeting` — it reads `limitation.maxQuantity` (licensed cap,
  // not the consumed `quantity`) and `ownerEmail` (account owner, not logged-in user).
  const passesRolloutTargeting = passesDevicesTabTargeting(company, ownerEmail);
  const showDevices =
    permissionsLoaded &&
    isRootSuperAdmin &&
    // Devices (MDM) is only supported when OptiSigns branding is kept — hidden on white-label
    // accounts that remove OptiSigns branding.
    !removeOptiSignsBranding &&
    (variant === 'optiedge' ||
      (variant === 'optisigns' && passesRolloutTargeting && (hasMdmFeature || mdmKillSwitchOn)));
  // scio1 shows Analytics for all admin roles regardless of plan — Standard users see
  // Playback Report with an upgrade prompt. Removing the proofOfPlay gate matches scio1.
  // Hidden on `optiedge` variant (MDM-only bundle).
  // scio1: hide the whole Analytics dropdown when white-label AND Proof-of-Play is hidden.
  const showAnalytics =
    permissionsLoaded &&
    isRootAdmin &&
    variant === 'optisigns' &&
    (!isWhiteLabel || !hideProofofPlay);

  // Collapse levels:
  // 1: search → icon-only
  // 2: logo → icon-only + user menu → avatar-only
  // 3: notifications + Get Started pill hidden
  // 5: engage + analytics → "More" dropdown; help + order-devices hidden
  // 6: optidev → "More" dropdown
  // 7: optisound → "More" dropdown
  /**
   * On the search RESULTS page the field stays a real search bar at every width
   * — it's the primary control of the page you're standing on, and collapsing it
   * to a magnifier there means the only way to amend a search is to reopen the
   * palette. Everything else yields two levels early to pay for it: the bar is
   * ~224px wider than the icon, and each threshold below is worth ~100px, so the
   * nav items fold into "More" a little sooner instead of the row overflowing.
   */
  const searchStaysExpanded = pathname === '/app/search';
  const itemLevel = searchStaysExpanded ? collapseLevel + 2 : collapseLevel;

  const compactSearch = collapseLevel >= 1 && !searchStaysExpanded;
  const compactLogo = itemLevel >= 2;
  const compactUser = itemLevel >= 2;
  const hideNotifications = itemLevel >= 3;
  // Help + Order Devices are primary actions — they survive two more
  // breakpoints than notifications so they stay visible as the nav narrows.
  const hideHelp = itemLevel >= 5;
  const analyticsInMore = itemLevel >= 5;
  const engageInMore = itemLevel >= 5;
  const optidevInMore = itemLevel >= 6;
  const optisoundInMore = itemLevel >= 7;
  // Levels 7-10: progressively fold the primary nav items into More so
  // they don't slide behind the right cluster (search/pill/cart/avatar)
  // and become unreachable. Right-to-left order matches the visual order
  // of the nav — Templates first (rightmost of the "Screens..Templates"
  // block), then Schedules, Playlists, Files/Assets. Screens stays as
  // the visible anchor at every width.
  const templatesInMore = itemLevel >= 7;
  const schedulesInMore = itemLevel >= 8;
  const playlistsInMore = itemLevel >= 9;
  const filesInMore = itemLevel >= 10;

  // Track which optional items overflow into "More". `showTemplates` /
  // `showAssets` etc. follow the same role+feature gating as the inline
  // NavLinks below, so referencing them keeps the visibility logic identical
  // between the inline render and the More dropdown.
  const hasMoreItems =
    (showAnalytics && analyticsInMore) ||
    (showEngage && engageInMore) ||
    (showOptiDev && optidevInMore) ||
    (showOptisound && optisoundInMore) ||
    (showTemplates && templatesInMore) ||
    schedulesInMore ||
    playlistsInMore ||
    filesInMore;

  return (
    <nav ref={navRef} className="sticky top-0 z-50 bg-muted">
      <div
        className={cn(
          'relative flex h-14 items-center overflow-visible px-2 md:px-4',
          // Onboarding lock — block every nav interaction and dim everything
          // EXCEPT the logo (first child stays at full opacity). The dev
          // Survey/Restart buttons re-enable pointer events on themselves.
          locked && 'pointer-events-none select-none [&>*:not(:first-child)]:opacity-60',
          // Paywall: same interaction block, no dimming and no placeholders.
          inert && 'pointer-events-none select-none',
        )}
        // Not hidden from assistive tech when merely inert — the blocking dialog is a
        // Radix modal, which already marks the rest of the page aria-hidden while open.
        aria-hidden={locked || undefined}
      >
        {/* Left: Logo — routes to the variant's primary landing page */}
        <Link
          to={variant === 'optiedge' ? '/app/mdm' : '/app/screenManagement'}
          className="shrink-0 flex items-center mr-3"
          data-testid="nav-logo-link"
        >
          {company?.brandingData?.logo ? (
            // Post-login header logo = the white-label `logo` (the "Branding → Logo" slot,
            // resolved to an S3 URL via the auth-store), mirroring scio1 `topNavigation.html`
            // (`ng-if="domain.logo"`). Empty string = no header logo → OptiSigns fallback below.
            // (`logoCompany` is the "Login Page → Logo" field, rendered by BrandLogo on auth screens.)
            <img
              src={company.brandingData.logo}
              alt={company.brandingData.siteTitle || 'Logo'}
              className={compactLogo ? 'h-7 w-7 object-contain' : 'h-7'}
            />
          ) : isWhiteLabel ? null : compactLogo ? ( // logo must never appear on a white-labeled account. // White-label without a custom logo → render nothing. The OptiSigns
            // Collapsed (narrow window) → OptiSigns cloud mark only (no wordmark).
            // Green mark, so a single asset works on both light and dark nav.
            <img src={OPTISIGNS_LOGO.mark} alt={t('OptiSigns')} className="h-6 w-auto ml-1 -mr-1" />
          ) : (
            // Full OptiSigns lockup. Light mode = dark-text PNG; dark mode = the
            // white-text lockup SVG (the PNG's dark text would vanish on dark nav).
            <>
              <img
                src={OPTISIGNS_LOGO.light}
                alt={t('OptiSigns')}
                className="h-6 w-auto dark:hidden"
              />
              <img
                src={OPTISIGNS_LOGO.dark}
                alt={t('OptiSigns')}
                className="hidden h-6 w-auto dark:block"
              />
            </>
          )}
        </Link>

        {/* Nav Items — horizontally scrollable on mobile, invisible no-op on desktop */}
        <div className="min-w-0 flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <div className="flex w-max items-center gap-0.5">
            {variant === 'optisigns' && (
              <>
                {/* Screens is the anchor — never folds into More. */}
                <NavLink to="/app/screenManagement" testId="nav-screens-link">
                  {t('Screens')}
                </NavLink>
                {!filesInMore && (
                  <NavLink to="/app/assetManagement" testId="nav-files-link">
                    {t('Files/Assets')}
                  </NavLink>
                )}
                {!playlistsInMore && (
                  <NavLink to="/app/playlistManagement" testId="nav-playlists-link">
                    {t('Playlists')}
                  </NavLink>
                )}
                {!schedulesInMore && (
                  <NavLink to="/app/scheduleManagement" testId="nav-schedules-link">
                    {t('Schedules')}
                  </NavLink>
                )}
              </>
            )}
            {locked ? (
              // Onboarding lock — everything past Schedules becomes grey
              // placeholder blocks so a brand-new user isn't shown
              // Templates / Engage / Devices / Analytics right away.
              <div className="ml-1.5 flex items-center gap-4" aria-hidden="true">
                {MINIMAL_NAV_PLACEHOLDERS.map((w, i) => (
                  <div key={i} className={cn('h-4 rounded-md bg-muted-foreground/15', w)} />
                ))}
              </div>
            ) : (
              <>
                {showTemplates && !templatesInMore && (
                  <NavLink to="/app/templateManagement" testId="nav-templates-link">
                    {t('Templates')}
                  </NavLink>
                )}

                {showEngage && !engageInMore && (
                  <NavLink to="/app/engageManagement" testId="nav-engage-link">
                    {t('Engage')}
                  </NavLink>
                )}
                {showOptiDev && !optidevInMore && <OptiDevNavLink active={showOptiDev} />}
                {showOptisound && !optisoundInMore && (
                  <NavLink to="/app/optisound" testId="nav-optisound-link">
                    {t('OptiSound')}
                  </NavLink>
                )}
                {/* Devices sits just before Analytics — init-doc mock §"Top menu". */}
                {showDevices && (
                  <NavLink to="/app/mdm" testId="nav-devices-link">
                    {t('Devices')}
                  </NavLink>
                )}
                {showAnalytics && !analyticsInMore && (
                  <AnalyticsDropdown
                    isWhiteLabel={isWhiteLabel}
                    hideProofofPlay={hideProofofPlay}
                  />
                )}

                {hasMoreItems && (
                  <MoreDropdown
                    showAnalytics={showAnalytics && analyticsInMore}
                    showEngage={showEngage && engageInMore}
                    showOptiDev={showOptiDev && optidevInMore}
                    showOptisound={showOptisound && optisoundInMore}
                    showTemplates={showTemplates && templatesInMore}
                    showSchedules={schedulesInMore && variant === 'optisigns'}
                    showPlaylists={playlistsInMore && variant === 'optisigns'}
                    showFiles={filesInMore && variant === 'optisigns'}
                    isWhiteLabel={isWhiteLabel}
                    hideProofofPlay={hideProofofPlay}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Right: Search + Help + Notifications + User Menu.
            Pill sits to the RIGHT of the search bar (try-out spot — earlier
            it was right of the bell; revert is just moving the pill-slot
            block back below the NotificationDropdown). */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {locked ? (
            // Drop the search-bar placeholder once the nav starts collapsing,
            // so it doesn't overlap the nav-item placeholder blocks at narrow widths.
            !compactSearch && <NavGhost className="h-9 w-44" />
          ) : (
            <GlobalSearchBar
              compact={compactSearch}
              // Only while the nav has room to spare: at collapse level 2+ the
              // logo and user menu are already shrinking, so a wider field would
              // be taking space from controls that just gave some up.
              wide={searchStaysExpanded && collapseLevel <= 1}
            />
          )}
          {locked ? (
            !hideHelp && !hideOptiSignsBranding && <NavGhost className="h-9 w-9" />
          ) : (
            <>{!hideHelp && !hideOptiSignsBranding && <HelpDropdown />}</>
          )}
          {!hideNotifications &&
            (locked ? <NavGhost className="h-9 w-9" /> : <NotificationDropdown />)}
          <UserMenu compact={compactUser} />
        </div>
      </div>
    </nav>
  );
}

/** TEMP dev-only — grey placeholder block standing in for a real nav control
 *  while the onboarding survey locks the top nav. Remove with the survey lock. */
function NavGhost({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('shrink-0 rounded-md bg-muted-foreground/15', className)}
    />
  );
}

/**
 * OptiDev top-nav tab. Renders the standard shared NavLink, except while the
 * per-user discovery highlight is active (`useFeatureHighlight`) — then it
 * renders in OptiDev brand purple with a matching underline-on-active. Each
 * click counts toward dismissal (after which it reverts to the plain NavLink).
 * Hover/focus use the SAME `bg-sidebar-active` background as the other nav tabs
 * (shared NavLink) — only the text + underline stay OptiDev purple.
 */
function OptiDevNavLink({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { highlighted, registerClick } = useFeatureHighlight('optidev', active);

  const to = '/app/optidev';

  if (!highlighted) {
    return (
      <NavLink to={to} testId="nav-optidev-link">
        {t('OptiDev')}
      </NavLink>
    );
  }

  return (
    <RouterNavLink
      to={to}
      data-testid="nav-optidev-link"
      // e2e hook: distinguishes the highlighted variant from the plain NavLink
      // (which shares the same testid). See optidev-highlight.spec.ts.
      data-highlighted="true"
      onClick={(e) => {
        registerClick();
        // Match shared NavLink: clicking the current page's tab is a no-op.
        if (pathname === to || pathname.startsWith(to + '/')) e.preventDefault();
      }}
      className={({ isActive }) =>
        cn(
          'relative inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:transition-colors',
          isActive
            ? 'text-[#9035f4] after:bg-[#9035f4]'
            : 'text-[#9035f4] after:bg-transparent hover:bg-sidebar-active focus-visible:bg-sidebar-active focus-visible:after:bg-[#9035f4]',
        )
      }
    >
      {t('OptiDev')}
    </RouterNavLink>
  );
}

const ANALYTICS_ROUTES = ['/app/playbackReport', '/app/ai-analytics'];

function AnalyticsDropdown({
  isWhiteLabel,
  hideProofofPlay,
}: {
  isWhiteLabel: boolean;
  hideProofofPlay: boolean;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isActive = ANALYTICS_ROUTES.some((route) => pathname.startsWith(route));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          data-testid="nav-analytics-trigger"
          className={cn(
            'relative inline-flex h-auto items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-colors after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:transition-colors',
            isActive
              ? 'text-foreground after:bg-primary'
              : 'text-muted-foreground after:bg-transparent hover:bg-sidebar-active hover:text-foreground focus-visible:bg-sidebar-active focus-visible:text-foreground focus-visible:after:bg-primary',
          )}
        >
          {t('Analytics')} <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={4}>
        {!hideProofofPlay && (
          <DropdownMenuItem asChild data-testid="nav-analytics-proof-of-play-item">
            <Link to="/app/playbackReport">{t('Proof of Play')}</Link>
          </DropdownMenuItem>
        )}
        {!isWhiteLabel && (
          <DropdownMenuItem asChild data-testid="nav-analytics-ai-analytics-item">
            <Link to="/app/ai-analytics">{t('AI Analytics')}</Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MoreDropdownProps {
  showAnalytics: boolean;
  showEngage: boolean;
  showOptiDev: boolean;
  showOptisound: boolean;
  showTemplates: boolean;
  showSchedules: boolean;
  showPlaylists: boolean;
  showFiles: boolean;
  isWhiteLabel: boolean;
  hideProofofPlay: boolean;
}

function MoreDropdown({
  showAnalytics,
  showEngage,
  showOptiDev,
  showOptisound,
  showTemplates,
  showSchedules,
  showPlaylists,
  showFiles,
  isWhiteLabel,
  hideProofofPlay,
}: MoreDropdownProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // Core nav items (Files/Playlists/Schedules/Templates) are listed BEFORE
  // optional features (Engage/OptiDev/Analytics) inside the dropdown so the
  // most-frequently-accessed pages stay at the top when the cascade
  // collapses them in. A separator marks the boundary between the two
  // groups visually.
  const hasCoreItems = showFiles || showPlaylists || showSchedules || showTemplates;
  const hasOptionalItems = showEngage || showOptiDev || showAnalytics;

  const moreRoutes = [
    ...(showFiles ? ['/app/assetManagement'] : []),
    ...(showPlaylists ? ['/app/playlistManagement'] : []),
    ...(showSchedules ? ['/app/scheduleManagement'] : []),
    ...(showTemplates ? ['/app/templateManagement'] : []),
    ...(showEngage ? ['/app/engageManagement'] : []),
    ...(showAnalytics ? ANALYTICS_ROUTES : []),
    ...(showOptiDev ? ['/app/optidev'] : []),
    ...(showOptisound ? ['/app/optisound'] : []),
  ];
  const isActive = moreRoutes.some((route) => pathname.startsWith(route));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          data-testid="nav-more-trigger"
          className={cn(
            'relative inline-flex h-auto items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-colors after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:transition-colors',
            isActive
              ? 'text-foreground after:bg-primary'
              : 'text-muted-foreground after:bg-transparent hover:bg-sidebar-active hover:text-foreground focus-visible:bg-sidebar-active focus-visible:text-foreground focus-visible:after:bg-primary',
          )}
        >
          <span>{t('More')}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={4}>
        {/* Core nav items folded in by the cascade (levels 7-10) */}
        {showFiles && (
          <DropdownMenuItem asChild data-testid="nav-more-files-item">
            <Link to="/app/assetManagement">{t('Files/Assets')}</Link>
          </DropdownMenuItem>
        )}
        {showPlaylists && (
          <DropdownMenuItem asChild data-testid="nav-more-playlists-item">
            <Link to="/app/playlistManagement">{t('Playlists')}</Link>
          </DropdownMenuItem>
        )}
        {showSchedules && (
          <DropdownMenuItem asChild data-testid="nav-more-schedules-item">
            <Link to="/app/scheduleManagement">{t('Schedules')}</Link>
          </DropdownMenuItem>
        )}
        {showTemplates && (
          <DropdownMenuItem asChild data-testid="nav-more-templates-item">
            <Link to="/app/templateManagement">{t('Templates')}</Link>
          </DropdownMenuItem>
        )}
        {/* Separator between core nav and optional features */}
        {hasCoreItems && hasOptionalItems && <DropdownMenuSeparator />}
        {showEngage && (
          <DropdownMenuItem asChild data-testid="nav-more-engage-item">
            <Link to="/app/engageManagement">{t('Engage')}</Link>
          </DropdownMenuItem>
        )}
        {showOptiDev && (
          <>
            {showEngage && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild data-testid="nav-more-optidev-item">
              <Link to="/app/optidev">{t('OptiDev')}</Link>
            </DropdownMenuItem>
          </>
        )}
        {showOptisound && (
          <>
            {(showEngage || showOptiDev) && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild data-testid="nav-more-optisound-item">
              <Link to="/app/optisound">{t('OptiSound')}</Link>
            </DropdownMenuItem>
          </>
        )}
        {showAnalytics && (
          <>
            {(showEngage || showOptiDev || showOptisound) && <DropdownMenuSeparator />}
            {!hideProofofPlay && (
              <DropdownMenuItem asChild data-testid="nav-more-proof-of-play-item">
                <Link to="/app/playbackReport">{t('Proof of Play')}</Link>
              </DropdownMenuItem>
            )}
            {!isWhiteLabel && (
              <DropdownMenuItem asChild data-testid="nav-more-ai-analytics-item">
                <Link to="/app/ai-analytics">{t('AI Analytics')}</Link>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
